<template>
  <div class="flex flex-fill">
    <b-card
        ref="card"
        class="flex-fill"
        header-bg-variant="primary">
      <template #header>
        <slot name="card-header">
          {{ header }}
        </slot>
      </template>

      <b-list-group class="flex-fill d-flex flex-column">
        <b-list-group-item
            v-if="!!$slots['header']"
            class="header">
          <slot name="header"/>
        </b-list-group-item>

        <b-list-group-item
            ref="content"
            class="content-scroll flex-fill no-margin no-padding">
          <div
              v-show="showContent"
              class="content">
            <slot name="content"/>
          </div>
        </b-list-group-item>

        <b-list-group-item
            v-if="!!$slots['footer']"
            class="p-0">
          <slot name="footer"/>
        </b-list-group-item>
      </b-list-group>
    </b-card>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Ref, Vue, Watch} from 'vue-property-decorator';
import {debounce, DebounceInstance} from 'vue-debounce';

@Component
export default class PageScalingCard extends Vue {
  @Prop({default: true, type: Boolean})
  public show!: boolean;

  @Prop({required: true, type: String})
  public header!: string;

  protected showContent = false;

  @Ref()
  private readonly content!: HTMLElement;

  // Empty debounce instance
  private debouncedResize: DebounceInstance = debounce(() => {
    // No-op
  }, 100);

  protected mounted(): void {
    // We need to create the handler here in order to correctly bind the this context
    this.debouncedResize = debounce(() => {
      this.determineContentSize();
    }, 100);

    // Set maximum height same as height after loading
    setTimeout(() => this.debouncedResize(), 100);

    // Setup debounced resize handler
    window.addEventListener('resize', this.debouncedResize);

    // Setup overlay scrollbar
    window.OverlayScrollbars(this.content, {});
  }

  // noinspection JSUnusedGlobalSymbols Vue lifecycle
  protected destroy(): void {
    window.removeEventListener('resize', this.debouncedResize);
  }

  @Watch('show', {immediate: true})
  private onShow(): void {
    if (!this.show) {
      return;
    }

    this.$nextTick(() => {
      this.debouncedResize();
    });
  }

  private determineContentSize(): void {
    if (!this.content) {
      return;
    }

    // Set maximum height same as height with no content
    this.showContent = false;
    this.content.style.maxHeight = 'none';
    this.$nextTick(() => {
      // Retrieve height in next tick, to ensure the table is removed
      this.content.style.maxHeight = this.content.getBoundingClientRect().height + 'px';
      this.showContent = true;
    });
  }

}
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";

.card {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;

  .list-group-item {
    border: 0 !important;

    border-bottom-right-radius: $list-group-border-radius;
    border-bottom-left-radius: $list-group-border-radius;
  }

  .card-header {
    border-bottom: 0 !important;
  }

  .card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 0;

    .content-scroll {
      min-height: 50vh;

      .content {
        margin: $spacer * 0.5;
      }
    }
  }
}
</style>
